import type { Settings } from "./settings-store.js";

/**
 * Where a {@link import("./settings-store.js").SettingsStore}'s blob is kept.
 * The one interface in the core that differs per host: the desktop writes a JSON
 * file, and a browser cannot (its worker has no synchronous storage of its own,
 * and the window-scoped one is not reachable from there at all).
 *
 * Two methods, both synchronous, and that is load-bearing rather than
 * incidental. Every settings accessor is synchronous and is read from inside
 * controllers that have no `await` to give — making persistence asynchronous
 * would ripple through all of them, so a host whose real storage is
 * asynchronous caches a snapshot behind this interface instead.
 *
 * A backend never throws. Nothing readable — a missing file, a corrupt one, a
 * blob that is not an object — reads as empty settings, so a bad store can
 * never stop the app launching. {@link parseSettings} is that rule, shared so
 * that every host applies it the same way.
 */
export interface SettingsBackend {
  /** The stored settings, or `{}` when there is nothing readable. */
  read(): Settings;
  /** Replace the stored settings wholesale. */
  write(settings: Settings): void;
}

/**
 * Read a stored JSON blob as settings, tolerating anything. Missing, corrupt or
 * not-an-object all yield empty settings rather than an exception.
 *
 * The values inside are deliberately *not* validated here: each accessor on the
 * store sanitises what it reads (`sanitiseWpm`, `sanitiseOverlayDefaults`, the
 * exactly-`true` rule for voice commands), so a hand-edited field is corrected
 * at the point it is used rather than silently dropped on load.
 */
export function parseSettings(raw: string | null | undefined): Settings {
  if (raw === null || raw === undefined) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Settings)
      : {};
  } catch {
    return {};
  }
}

/**
 * A backend holding the blob in memory. What tests use, and the base a host
 * with asynchronous real storage builds on — the browser's core worker caches
 * the page's snapshot in one of these and posts each write back out to be
 * persisted.
 *
 * It copies in both directions, so neither a reference kept by whoever wrote a
 * value nor one handed back by a read can reach into what is stored. A file
 * backend gets that for free by serialising; without it here the two backends
 * would not behave the same way, and the difference would only surface as a bug
 * on one host.
 */
export class MemorySettingsBackend implements SettingsBackend {
  #settings: Settings;

  constructor(settings: Settings = {}) {
    this.#settings = structuredClone(settings);
  }

  read(): Settings {
    return structuredClone(this.#settings);
  }

  write(settings: Settings): void {
    this.#settings = structuredClone(settings);
  }
}
